/**
 * Confirmation Dialog Component
 *
 * A configurable confirmation dialog that can include form fields for additional
 * input during confirmation. Used for confirming actions with additional input fields.
 *
 * Features:
 * - Configurable dialog content and buttons
 * - Form fields integration via component loader
 * - Promise-based API for easy integration
 * - Escape key handling
 * - State management integration
 *
 * Note: This component is designed to be used as a single-use instance.
 * Create a fresh instance for each dialog to avoid state issues.
 *
 * @example
 * ```html
 * <uap-app-integration-settings-confirm-dialog>
 * </uap-app-integration-settings-confirm-dialog>
 *
 * <script>
 * const dialog = document.createElement('uap-app-integration-settings-confirm-dialog');
 * document.body.appendChild(dialog);
 *
 * const result = await dialog.show({
 *     heading: 'Delete Account',
 *     content: 'Are you sure you want to delete this account?',
 *     confirmButtonLabel: 'Delete',
 *     formFields: [
 *         { type: 'text-field', name: 'reason', label: 'Reason for deletion' }
 *     ]
 * });
 *
 * if (result.confirmed) {
 *     console.log('Form data:', result.formData);
 * }
 * </script>
 * ```
 *
 * @since 6.9.0 Initial implementation
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _AutomatorAppIntegrationSettingsConfirmDialog_open_accessor_storage, _AutomatorAppIntegrationSettingsConfirmDialog_heading_accessor_storage, _AutomatorAppIntegrationSettingsConfirmDialog_content_accessor_storage, _AutomatorAppIntegrationSettingsConfirmDialog_confirmButtonLabel_accessor_storage, _AutomatorAppIntegrationSettingsConfirmDialog_cancelButtonLabel_accessor_storage, _AutomatorAppIntegrationSettingsConfirmDialog_formFields_accessor_storage, _AutomatorAppIntegrationSettingsConfirmDialog_forceManualClose_accessor_storage;
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { escapeHTML } from '@wordpress/escape-html';
import { __, _x } from '@wordpress/i18n';
// Import styles
import styles from './web-component-style.scss?inline';
// Import utilities
import { collectFormData } from '../../utils/form-data-collector';
import { initStateManager } from '../../utils/state-manager';
// Import the component loader
import '../component-loader';
/**
 * Confirmation Dialog Component
 * @element uap-app-integration-settings-confirm-dialog
 */
let AutomatorAppIntegrationSettingsConfirmDialog = class AutomatorAppIntegrationSettingsConfirmDialog extends LitElement {
    constructor() {
        super(...arguments);
        _AutomatorAppIntegrationSettingsConfirmDialog_open_accessor_storage.set(this, false);
        _AutomatorAppIntegrationSettingsConfirmDialog_heading_accessor_storage.set(this, '');
        _AutomatorAppIntegrationSettingsConfirmDialog_content_accessor_storage.set(this, '');
        _AutomatorAppIntegrationSettingsConfirmDialog_confirmButtonLabel_accessor_storage.set(this, '');
        _AutomatorAppIntegrationSettingsConfirmDialog_cancelButtonLabel_accessor_storage.set(this, '');
        _AutomatorAppIntegrationSettingsConfirmDialog_formFields_accessor_storage.set(this, '');
        _AutomatorAppIntegrationSettingsConfirmDialog_forceManualClose_accessor_storage.set(this, false);
        /**
         * Handle escape key press to close dialog
         *
         * @param {KeyboardEvent} event - The keyboard event
         *
         * @return {void}
         */
        this.handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                // Prevent the legacy AutomatorDialog escape handler from interfering
                event.stopPropagation();
                event.preventDefault();
                // Close our dialog
                this.open = false;
                if (this.rejectPromise) {
                    this.rejectPromise(new Error('Confirmation cancelled by user.'));
                }
            }
        };
    }
    /**
     * Whether the dialog is open
     *
     * @type {boolean}
     */
    get open() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsConfirmDialog_open_accessor_storage, "f"); }
    set open(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsConfirmDialog_open_accessor_storage, value, "f"); }
    /**
     * The dialog heading
     *
     * @type {string}
     */
    get heading() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsConfirmDialog_heading_accessor_storage, "f"); }
    set heading(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsConfirmDialog_heading_accessor_storage, value, "f"); }
    /**
     * The dialog content
     *
     * @type {string}
     */
    get content() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsConfirmDialog_content_accessor_storage, "f"); }
    set content(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsConfirmDialog_content_accessor_storage, value, "f"); }
    /**
     * Label for the confirm button
     *
     * @type {string}
     */
    get confirmButtonLabel() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsConfirmDialog_confirmButtonLabel_accessor_storage, "f"); }
    set confirmButtonLabel(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsConfirmDialog_confirmButtonLabel_accessor_storage, value, "f"); }
    /**
     * Label for the cancel button
     *
     * @type {string}
     */
    get cancelButtonLabel() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsConfirmDialog_cancelButtonLabel_accessor_storage, "f"); }
    set cancelButtonLabel(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsConfirmDialog_cancelButtonLabel_accessor_storage, value, "f"); }
    /**
     * Form fields configuration (JSON string)
     *
     * @type {string}
     */
    get formFields() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsConfirmDialog_formFields_accessor_storage, "f"); }
    set formFields(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsConfirmDialog_formFields_accessor_storage, value, "f"); }
    /**
     * Whether to force manual close (prevent ESC key)
     *
     * @type {boolean}
     */
    get forceManualClose() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsConfirmDialog_forceManualClose_accessor_storage, "f"); }
    set forceManualClose(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsConfirmDialog_forceManualClose_accessor_storage, value, "f"); }
    /**
     * Parse form fields from JSON string
     *
     * @return {ConfirmFormField[]} Array of form field configurations
     */
    parseFormFields() {
        if (!this.formFields) {
            return [];
        }
        try {
            return JSON.parse(this.formFields);
        }
        catch {
            console.warn('Invalid form fields JSON:', this.formFields);
            return [];
        }
    }
    /**
     * Create loader config from form fields
     *
     * @return {LoaderConfig} The loader configuration
     */
    createLoaderConfig() {
        const fields = this.parseFormFields();
        return {
            components: fields.map(field => ({
                type: field.type,
                data: { ...field },
                className: field.className,
            }))
        };
    }
    /**
     * Collect form data from the form element and handle multiple values per field
     *
     * Form fields can have multiple values with the same name (e.g., checkboxes, multi-selects).
     * This method transforms FormData entries into a clean object where:
     * - Single values remain as strings
     * - Multiple values become string arrays
     *
     * @return {Record<string, string | string[]>} Object with field names as keys and values as strings or string arrays
     */
    collectFormData() {
        if (!this.formElement) {
            return {};
        }
        const formData = collectFormData(this.formElement);
        const result = {};
        // Process each form field, handling cases where multiple values exist for the same field name
        for (const [key, value] of formData.entries()) {
            const currentValue = result[key];
            // First occurrence of this field name - store as single string value
            if (!currentValue) {
                result[key] = value;
                continue;
            }
            // Multiple values detected - convert to array format
            // If already an array, append to it; otherwise create new array with both values
            result[key] = Array.isArray(currentValue)
                ? [...currentValue, value] // Append to existing array
                : [currentValue, value]; // Convert single value + new value to array
        }
        return result;
    }
    /**
     * Handle confirm button click
     *
     * @param {Event} [event] - The click event
     *
     * @return {void}
     */
    handleConfirm(event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const formData = this.collectFormData();
        this.open = false;
        if (this.resolvePromise) {
            this.resolvePromise({ confirmed: true, formData });
        }
    }
    /**
     * Handle cancel button click
     *
     * @param {Event} [event] - The click event
     *
     * @return {void}
     */
    handleCancel(event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.open = false;
        if (this.rejectPromise) {
            this.rejectPromise(new Error('Confirmation cancelled by user.'));
        }
    }
    /**
     * Handle dialog close event (ESC key, click outside, etc.)
     *
     * @param {CustomEvent} event - The close event
     *
     * @return {void}
     */
    handleClose(event) {
        const wasCancelled = event.detail?.was_cancelled;
        if (wasCancelled && this.rejectPromise) {
            this.rejectPromise(new Error('Confirmation cancelled by user.'));
        }
    }
    /**
     * Show the dialog and return a promise
     *
     * @param {ConfirmDialogConfig} [config={}] - The dialog configuration
     *
     * @return {Promise<ConfirmDialogResult>} Promise that resolves with the dialog result
     */
    show(config = {}) {
        // Update properties from config
        this.heading = config.heading || escapeHTML(__('Are you sure?', 'uncanny-automator'));
        this.content = config.content || '';
        this.confirmButtonLabel = config.confirmButtonLabel || escapeHTML(_x('Confirm', 'Verb', 'uncanny-automator'));
        this.cancelButtonLabel = config.cancelButtonLabel || escapeHTML(_x('Cancel', 'Verb', 'uncanny-automator'));
        this.formFields = config.formFields ? JSON.stringify(config.formFields) : '';
        // Open the dialog
        this.open = true;
        // Return a promise
        return new Promise((resolve, reject) => {
            this.resolvePromise = resolve;
            this.rejectPromise = reject;
        });
    }
    /**
     * Initialize state management after form is rendered
     *
     * @param {Map<string, unknown>} changedProperties - Map of changed properties
     *
     * @return {void}
     */
    updated(changedProperties) {
        super.updated(changedProperties);
        // Only handle open property changes
        if (!changedProperties.has('open')) {
            return;
        }
        // Dialog is opening - add listener and initialize state management
        if (this.open) {
            document.addEventListener('keydown', this.handleEscapeKey, { capture: true });
            if (this.formElement) {
                this.updateComplete.then(() => {
                    setTimeout(() => {
                        if (this.formElement) {
                            initStateManager(this.formElement);
                        }
                    }, 50);
                });
            }
            return;
        }
        // Dialog is closing - remove listener
        document.removeEventListener('keydown', this.handleEscapeKey, { capture: true });
    }
    /**
     * Cleanup when component is removed from DOM
     *
     * @return {void}
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        // Ensure escape key listener is removed if component is destroyed while open
        document.removeEventListener('keydown', this.handleEscapeKey, { capture: true });
    }
    /**
     * Render the dialog content
     *
     * @return {TemplateResult} The rendered dialog content template
     */
    renderDialogContent() {
        const fields = this.parseFormFields();
        const loaderConfig = this.createLoaderConfig();
        return html `
			${this.content ? html `<div class="confirm-dialog__content">${this.content}</div>` : ''}
			${fields.length > 0 ? html `
				<form id="confirm-fields-form" class="confirm-dialog__form">
					<uap-app-integration-settings-component-loader .config=${loaderConfig}></uap-app-integration-settings-component-loader>
				</form>
			` : ''}
			`;
    }
    /**
     * Render the dialog buttons
     *
     * @return {TemplateResult} The rendered dialog buttons template
     */
    renderDialogButtons() {
        return html `
			<uo-button slot="primary-action" @click=${this.handleConfirm}>
				${this.confirmButtonLabel}
			</uo-button>
			<uo-button slot="secondary-action" color="secondary" @click=${this.handleCancel}>
				${this.cancelButtonLabel}
			</uo-button>
		`;
    }
    /**
     * Render the component
     *
     * @return {TemplateResult} The rendered component template
     */
    render() {
        if (!this.open) {
            return html ``;
        }
        return html `
			<uo-dialog
				.heading=${this.heading}
				?force-manual-close=${this.forceManualClose}
				open
				@uap-dialog-close=${this.handleClose}
			>
				${this.renderDialogContent()}
				${this.renderDialogButtons()}
			</uo-dialog>
		`;
    }
};
_AutomatorAppIntegrationSettingsConfirmDialog_open_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsConfirmDialog_heading_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsConfirmDialog_content_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsConfirmDialog_confirmButtonLabel_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsConfirmDialog_cancelButtonLabel_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsConfirmDialog_formFields_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsConfirmDialog_forceManualClose_accessor_storage = new WeakMap();
AutomatorAppIntegrationSettingsConfirmDialog.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({ type: Boolean })
], AutomatorAppIntegrationSettingsConfirmDialog.prototype, "open", null);
__decorate([
    property({ type: String })
], AutomatorAppIntegrationSettingsConfirmDialog.prototype, "heading", null);
__decorate([
    property({ type: String })
], AutomatorAppIntegrationSettingsConfirmDialog.prototype, "content", null);
__decorate([
    property({ type: String })
], AutomatorAppIntegrationSettingsConfirmDialog.prototype, "confirmButtonLabel", null);
__decorate([
    property({ type: String })
], AutomatorAppIntegrationSettingsConfirmDialog.prototype, "cancelButtonLabel", null);
__decorate([
    property({ type: String })
], AutomatorAppIntegrationSettingsConfirmDialog.prototype, "formFields", null);
__decorate([
    property({ type: Boolean, attribute: 'force-manual-close' })
], AutomatorAppIntegrationSettingsConfirmDialog.prototype, "forceManualClose", null);
__decorate([
    query('#confirm-fields-form')
], AutomatorAppIntegrationSettingsConfirmDialog.prototype, "formElement", void 0);
AutomatorAppIntegrationSettingsConfirmDialog = __decorate([
    customElement('uap-app-integration-settings-confirm-dialog')
], AutomatorAppIntegrationSettingsConfirmDialog);
export { AutomatorAppIntegrationSettingsConfirmDialog };
